import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Trash2, AlertTriangle, X } from "lucide-react";
import api, { clearAuthTokens } from "@/utils/api";
import { useUser } from "@/context/UserContext";

export default function DeleteAccountSection() {
  const { logout } = useUser();
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState("");
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState("");

  const handleDelete = async (e) => {
    e.preventDefault();
    if (!password) {
      setError("Bitte gib dein Passwort ein.");
      return;
    }
    setError("");
    setDeleting(true);
    try {
      await api.delete("/users/me", { data: { password } });
      // Tokens sauber entfernen, Context leeren und zur Startseite
      clearAuthTokens();
      if (typeof logout === "function") {
        logout();
      } else {
        localStorage.clear();
      }
      window.location.href = "/";
    } catch (err) {
      setError(err.response?.data?.detail || "Konto konnte nicht gelöscht werden.");
      setDeleting(false);
    }
  };

  return (
    <>
      <div className="bg-white rounded-2xl border border-red-200 shadow-sm p-6 mt-6">
        <h2 className="text-lg font-bold text-red-700 mb-2 flex items-center gap-2">
          <AlertTriangle size={20} />
          Gefahrenzone
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          Das Löschen deines Kontos entfernt dein Profil, deine Anfragen und alle hochgeladenen Dateien dauerhaft. Diese Aktion kann nicht rückgängig gemacht werden.
        </p>
        <button
          onClick={() => setOpen(true)}
          data-testid="delete-account-open-btn"
          className="flex items-center gap-2 px-4 py-2 bg-red-50 text-red-700 border border-red-200 rounded-xl hover:bg-red-100 transition-colors text-sm font-medium"
        >
          <Trash2 size={16} />
          Konto löschen
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
            onClick={() => !deleting && setOpen(false)}
            data-testid="delete-account-modal"
          >
            <motion.form
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 20, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              onSubmit={handleDelete}
              className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6"
            >
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                    <AlertTriangle size={20} className="text-red-600" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Konto wirklich löschen?</h3>
                </div>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  disabled={deleting}
                  className="text-gray-400 hover:text-gray-600 p-1"
                  aria-label="Schließen"
                >
                  <X size={20} />
                </button>
              </div>

              <p className="text-sm text-gray-600 mb-4">
                Diese Aktion ist <strong>endgültig</strong>. Dein Profil, deine Anfragen und alle Dateien werden gelöscht. Bitte bestätige mit deinem Passwort.
              </p>

              <label className="block">
                <span className="text-sm font-medium text-gray-700">Passwort zur Bestätigung</span>
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  autoFocus
                  disabled={deleting}
                  data-testid="delete-account-password-input"
                  className="mt-1 w-full px-4 py-2.5 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-red-500"
                  placeholder="Dein aktuelles Passwort"
                />
              </label>

              {error && (
                <div data-testid="delete-account-error" className="mt-3 bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded-lg">
                  {error}
                </div>
              )}

              <div className="flex gap-2 mt-6">
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  disabled={deleting}
                  className="flex-1 px-4 py-2.5 bg-gray-100 text-gray-700 rounded-xl hover:bg-gray-200 transition-colors text-sm font-medium"
                >
                  Abbrechen
                </button>
                <button
                  type="submit"
                  disabled={deleting || !password}
                  data-testid="delete-account-confirm-btn"
                  className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deleting ? "Wird gelöscht…" : "Konto löschen"}
                </button>
              </div>
            </motion.form>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
